import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Coffee, Sun, Cookie, Salad, UtensilsCrossed, Check, Square, ClipboardList, X, Copy, Pencil, Beef, FileDown } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { recipes } from '../data/recipes';

const STORE_KEY = 'gw_dd_plan_v2';

const MEALS = [
  { key: 'drink', label: 'Tea / Elixir', Icon: Coffee, cats: ['teas'] },
  { key: 'breakfast', label: 'Breakfast', Icon: Sun, special: true },
  { key: 'snack', label: 'Snack', Icon: Cookie, cats: ['snacks'] },
  { key: 'lunch', label: 'Lunch', Icon: Salad, cats: ['salads'] },
  { key: 'dinner', label: 'Dinner', Icon: UtensilsCrossed, cats: ['mains', 'soups'] },
];
const mealByKey = Object.fromEntries(MEALS.map((m) => [m.key, m]));

// Fixed extras (apply every day)
const SNACK_EXTRAS = [{ id: 'half-smoothie', title: '½ smoothie' }, { id: 'half-lunch', title: '½ lunch' }];
const PROTEINS = ['Salmon', 'Turkey', 'Steak', 'Chicken', 'Ground meat'];

// Day-level detox boost (shows at the bottom of each day and collects into the plan)
const BOOST_MEAL = { key: 'boost', label: 'Detox Boost' };
const BROTH_ADDON = { id: 'detox-broth', title: 'Sub 1 meal or snack with 1 cup homemade broth (veg or bone broth)', link: false };


// Per-day curated overrides (recipe ids). Anything not listed uses the auto pick.
const DAY_OVERRIDES = {
  '1-drink': ['lemon-elixir', 'clove-ginger-detox-tea'],
  '1-breakfast': ['whole30-chia-pudding', 'egg-muffin-cups', 'blueberry-spinach-smoothie'],
  '1-snack': ['banana-bread', 'magic-bars', 'yogurt-and-nuts'],
  '3-drink': ['nighttime-calm-tea', 'ginger-turmeric-zest-tea'],
  '3-breakfast': ['whole30-chia-pudding', 'chunky-monkey-smoothie', 'egg-muffin-cups'],
  '3-snack': ['yogurt-and-nuts', 'magic-bars'],
  '3-lunch': ['gut-glow-crunch-salad', 'celery-salad', 'carrot-salad'],
  '3-dinner': ['cold-pea-watercress-soup', 'almond-salmon-swiss-chard', 'shawarma-chicken'],
  '4-breakfast': ['berry-yogurt-bowl', 'egg-power-bowl', 'blueberry-spinach-smoothie'],
  '4-snack': ['magic-bars', 'purple-slaw'],
  '4-lunch': ['celery-salad', 'carrot-salad', 'spicy-chicken-salad'],
  '4-dinner': ['roasted-chicken-root-veg', 'nettle-cabbage-soup', 'whole-roasted-cauliflower'],
  '5-breakfast': ['stewed-apples', 'blueberry-spinach-smoothie', 'egg-muffin-cups'],
  '5-snack': ['celery-salad', 'banana-bread', 'magic-bars'],
  '5-lunch': ['carrot-salad', 'fruit-vegetable-salad', 'chicken-satay-bowl'],
  '5-dinner': ['spring-pea-soup', 'cauliflower-black-bean-bowl', 'coconut-chicken-rice-bowl'],
  '6-breakfast': ['egg-power-bowl', 'stewed-apples', 'blueberry-spinach-smoothie'],
  '6-snack': ['carrot-salad', 'yogurt-and-nuts'],
  '6-lunch': ['celery-salad', 'taco-salad', 'fruit-vegetable-salad'],
  '6-dinner': ['magic-chili', 'rosemary-roasted-cauliflower-tahini', 'roasted-root-vegetable-soup'],
  '7-breakfast': ['mixed-berries', 'tanzanian-banana-coconut', 'egg-power-bowl'],
  '7-snack': ['magic-bars', 'banana-bread', { id: 'one-fruit-nuts', title: 'One fruit + nuts', link: false }],
  '7-dinner': ['beet-salad-roasted-fennel-blood-orange', 'roasted-zucchini-soup', 'grilled-salmon-veggies', 'napa-cabbage-salad'],
};

const RECIPE_TITLE = Object.fromEntries(recipes.map((r) => [r.id, r.title]));

const inCategory = (r, cats) => cats.includes(r.category) || (Array.isArray(r.categories) && r.categories.some((c) => cats.includes(c)));
const isDetox = (r) => Array.isArray(r.tags) && r.tags.includes('Detox Friendly');

const poolFor = (cats) => {
  let pool = recipes.filter((r) => inCategory(r, cats) && isDetox(r));
  if (pool.length === 0) pool = recipes.filter((r) => inCategory(r, cats));
  return pool.map((r) => ({ id: r.id, title: r.title }));
};

const seededPick = (pool, seed, n) => {
  const a = [...pool];
  let s = seed;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, Math.min(n, a.length));
};

const buildOptions = () => {
  const drinkPool = poolFor(['teas']);
  const smoothiePool = poolFor(['smoothies']);
  const proteinPool = poolFor(['breakfast']);
  const snackPool = poolFor(['snacks', 'salads']);
  const lunchPool = poolFor(['salads']);
  const dinnerPool = poolFor(['mains', 'soups']);
  const opts = {};
  for (let day = 1; day <= 7; day++) {
    opts[`${day}-drink`] = seededPick(drinkPool, day * 31 + 1, 3);
    opts[`${day}-breakfast`] = [...seededPick(proteinPool, day * 53 + 7, 1), ...seededPick(smoothiePool, day * 71 + 3, 2)];
    opts[`${day}-snack`] = seededPick(snackPool, day * 97 + 5, 3);
    opts[`${day}-lunch`] = seededPick(lunchPool, day * 113 + 9, 3);
    opts[`${day}-dinner`] = seededPick(dinnerPool, day * 131 + 11, 3);
  }
  // Apply curated overrides. Entries may be a recipe id (string) or a custom
  // { id, title, link:false } object for plain non-recipe text (e.g. "One fruit + nuts").
  Object.entries(DAY_OVERRIDES).forEach(([k, entries]) => {
    opts[k] = entries.map((e) =>
      typeof e === 'string'
        ? { id: e, title: RECIPE_TITLE[e] || e }
        : { id: e.id, title: e.title || RECIPE_TITLE[e.id] || e.id, link: e.link }
    );
  });
  return opts;
};

export const DoubleDownMealMap = () => {
  const options = useMemo(buildOptions, []);
  const [store, setStore] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || { selected: {}, notes: {} }; } catch { return { selected: {}, notes: {} }; }
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => { localStorage.removeItem('gw_dd_plan'); }, []);

  const save = (next) => { setStore(next); localStorage.setItem(STORE_KEY, JSON.stringify(next)); };
  const selKey = (day, meal, id) => `${day}|${meal}|${id}`;

  const toggle = (day, meal, opt) => {
    const k = selKey(day, meal, opt.id);
    const selected = { ...store.selected };
    if (selected[k]) delete selected[k];
    else selected[k] = { day, meal, id: opt.id, title: opt.title, link: opt.link !== false };
    save({ ...store, selected });
  };
  const setNote = (key, val) => save({ ...store, notes: { ...store.notes, [key]: val } });
  const removeSel = (k) => { const selected = { ...store.selected }; delete selected[k]; save({ ...store, selected }); };
  const clearAll = () => save({ selected: {}, notes: {} });

  const isChecked = (day, meal, id) => !!store.selected[selKey(day, meal, id)];

  const CheckRow = ({ day, meal, opt, indent }) => {
    const checked = isChecked(day, meal, opt.id);
    return (
      <li key={opt.id} className={`flex items-start gap-2 ${indent ? 'pl-1' : ''}`}>
        <button
          onClick={() => toggle(day, meal, opt)}
          className={`mt-0.5 flex-shrink-0 transition-colors ${checked ? 'text-forest' : 'text-stone-300 hover:text-forest'}`}
          data-testid={`dd-check-${day}-${meal}-${opt.id}`}
        >
          {checked ? <Check size={16} /> : <Square size={16} />}
        </button>
        {opt.link !== false ? (
          <Link to={`/resources/recipes?resource=${opt.id}`} className={`font-body text-sm leading-snug transition-colors ${checked ? 'text-forest font-medium' : 'text-stone-600 hover:text-terracotta'}`}>{opt.title}</Link>
        ) : (
          <span className={`font-body text-sm leading-snug ${checked ? 'text-forest font-medium' : 'text-stone-600'}`}>{opt.title}</span>
        )}
      </li>
    );
  };

  const renderSlot = (day, meal) => {
    const opts = options[`${day}-${meal.key}`] || [];
    const Icon = meal.Icon;
    return (
      <div key={meal.key} data-testid={`dd-slot-${day}-${meal.key}`}>
        <div className="flex items-center gap-1.5 mb-2">
          <Icon size={13} className="text-stone-400" />
          <span className="text-[11px] font-body uppercase tracking-wider text-stone-500">{meal.label}</span>
        </div>
        <ul className="space-y-1.5">
          {opts.map((opt) => CheckRow({ day, meal: meal.key, opt }))}
          {meal.key === 'snack' && SNACK_EXTRAS.map((opt) => CheckRow({ day, meal: 'snack', opt: { ...opt, link: false } }))}
        </ul>

        {meal.key === 'lunch' && (
          <div className="mt-2.5 pl-2 border-l-2 border-amber-200">
            <div className="flex items-center gap-1 mb-1">
              <Beef size={11} className="text-stone-400" />
              <span className="text-[10px] font-body uppercase tracking-wider text-stone-400">Add protein</span>
            </div>
            <ul className="space-y-1">
              {PROTEINS.map((p) => CheckRow({ day, meal: 'lunch', opt: { id: `protein-${p.toLowerCase().replace(/\s+/g, '-')}`, title: p, link: false }, indent: true }))}
            </ul>
            <input
              type="text"
              value={store.notes[`${day}|lunch|protein-custom`] || ''}
              onChange={(e) => setNote(`${day}|lunch|protein-custom`, e.target.value)}
              placeholder="＋ other protein…"
              className="mt-1 w-full text-xs font-body text-stone-700 bg-stone-50 border border-stone-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-terracotta placeholder:text-stone-400"
              data-testid={`dd-protein-note-${day}`}
            />
          </div>
        )}

        <input
          type="text"
          value={store.notes[`${day}|${meal.key}`] || ''}
          onChange={(e) => setNote(`${day}|${meal.key}`, e.target.value)}
          placeholder="＋ other idea…"
          className="mt-2 w-full text-xs font-body text-stone-700 bg-stone-50 border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-terracotta placeholder:text-stone-400"
          data-testid={`dd-note-${day}-${meal.key}`}
        />
      </div>
    );
  };

  // Build plan grouped by day
  const planDays = [1, 2, 3, 4, 5, 6, 7].map((day) => {
    const meals = [...MEALS, BOOST_MEAL].map((m) => {
      const items = Object.entries(store.selected)
        .filter(([, v]) => v.day === day && v.meal === m.key)
        .map(([k, v]) => ({ k, ...v }));
      const notes = [];
      const idea = (store.notes[`${day}|${m.key}`] || '').trim();
      if (idea) notes.push(idea);
      if (m.key === 'lunch') {
        const prot = (store.notes[`${day}|lunch|protein-custom`] || '').trim();
        if (prot) notes.push(`protein: ${prot}`);
      }
      return { meal: m, items, notes };
    }).filter((x) => x.items.length || x.notes.length);
    return { day, meals };
  }).filter((d) => d.meals.length);

  const copyPlan = async () => {
    const text = planDays.map((d) =>
      `Day ${d.day}\n` + d.meals.map((mm) => {
        const parts = mm.items.map((i) => i.title).concat(mm.notes.map((n) => `(${n})`));
        return `  ${mm.meal.label}: ${parts.join(', ')}`;
      }).join('\n')
    ).join('\n\n');
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 48;
    let y = margin;
    const ensureSpace = (needed) => { if (y + needed > pageH - margin) { doc.addPage(); y = margin; } };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(44, 62, 46);
    doc.text('The Double Down', margin, y);
    y += 24;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    doc.setTextColor(140, 110, 90);
    doc.text('My 7-Day Meal Plan', margin, y);
    y += 20;
    doc.setDrawColor(220, 210, 180);
    doc.line(margin, y, pageW - margin, y);
    y += 22;

    planDays.forEach((d) => {
      ensureSpace(46);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(44, 62, 46);
      doc.text(`Day ${d.day}`, margin, y);
      y += 18;
      d.meals.forEach((mm) => {
        ensureSpace(28);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(150, 120, 90);
        doc.text(mm.meal.label.toUpperCase(), margin, y);
        y += 14;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(60, 60, 60);
        const lines = mm.items.map((i) => i.title).concat(mm.notes.map((n) => `"${n}"`));
        lines.forEach((ln) => {
          const wrapped = doc.splitTextToSize(`•  ${ln}`, pageW - margin * 2 - 12);
          wrapped.forEach((w) => { ensureSpace(16); doc.text(w, margin + 10, y); y += 15; });
        });
        y += 6;
      });
      y += 12;
    });

    doc.save('double-down-meal-plan.pdf');
  };

  return (
    <div className="space-y-8" data-testid="double-down-meal-map">
      <p className="font-body text-sm text-stone-600 leading-relaxed max-w-3xl">
        Tick the meals you like for each day and they’ll collect into your clean plan at the bottom. Breakfast offers a protein
        option plus a couple of smoothies. Under Snack you can also grab a ½ smoothie or ½ lunch, and under Lunch you can add a
        protein. Want something else? Use the <Pencil size={12} className="inline -mt-0.5" /> note line to write your own idea.
      </p>

      <div className="space-y-4">
        {[1, 2, 3, 4, 5, 6, 7].map((day) => (
          <div key={day} className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid={`dd-meal-day-${day}`}>
            <div className="bg-gradient-to-r from-amber-50 to-emerald-50 px-5 py-3 border-b border-stone-200">
              <h4 className="font-heading text-lg text-forest">Day {day}</h4>
            </div>
            <div className="p-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {MEALS.map((meal) => renderSlot(day, meal))}
            </div>
            <div className="border-t border-emerald-100 bg-emerald-50/50 px-4 py-3" data-testid={`dd-boost-${day}`}>
              <p className="text-[10px] font-body uppercase tracking-wider text-emerald-700/80 mb-1">Up your detox game</p>
              <ul>
                {CheckRow({ day, meal: 'boost', opt: BROTH_ADDON })}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* MY PLAN */}
      <div className="bg-gradient-to-br from-amber-50 to-emerald-50 rounded-2xl border border-stone-200 p-5 md:p-6" data-testid="dd-my-plan">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ClipboardList size={20} className="text-forest" />
            <h3 className="font-heading text-xl text-forest">My Plan</h3>
          </div>
          {planDays.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={copyPlan} className="inline-flex items-center gap-1.5 bg-forest text-cream px-3 py-1.5 rounded-full text-xs font-body font-medium hover:bg-forest-dark transition-colors" data-testid="dd-plan-copy">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={downloadPdf} className="inline-flex items-center gap-1.5 bg-terracotta text-white px-3 py-1.5 rounded-full text-xs font-body font-medium hover:bg-terracotta-dark transition-colors" data-testid="dd-plan-pdf">
                <FileDown size={13} /> PDF
              </button>
              <button onClick={clearAll} className="text-stone-500 hover:text-red-600 text-xs font-body transition-colors" data-testid="dd-plan-clear">Clear</button>
            </div>
          )}
        </div>
        {planDays.length === 0 ? (
          <p className="font-body text-sm text-stone-500">Your plan is empty. Tick any meals above (or add your own idea) and they’ll collect here by day.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {planDays.map((d) => (
              <div key={d.day} className="bg-white rounded-xl border border-stone-200 p-4" data-testid={`dd-plan-day-${d.day}`}>
                <p className="font-heading text-base text-forest mb-2">Day {d.day}</p>
                <div className="space-y-2">
                  {d.meals.map((mm) => (
                    <div key={mm.meal.key}>
                      <p className="text-[10px] font-body uppercase tracking-wider text-stone-400 mb-0.5">{mm.meal.label}</p>
                      <ul className="space-y-1">
                        {mm.items.map((i) => (
                          <li key={i.k} className="flex items-start gap-2 group">
                            {i.link ? (
                              <Link to={`/resources/recipes?resource=${i.id}`} className="flex-1 font-body text-sm text-forest hover:text-terracotta transition-colors leading-snug">{i.title}</Link>
                            ) : (
                              <span className="flex-1 font-body text-sm text-forest leading-snug">{i.title}</span>
                            )}
                            <button onClick={() => removeSel(i.k)} className="text-stone-300 hover:text-red-500 transition-colors flex-shrink-0" title="Remove" data-testid={`dd-plan-remove-${i.k}`}>
                              <X size={13} />
                            </button>
                          </li>
                        ))}
                        {mm.notes.map((n, ni) => (
                          <li key={`n${ni}`} className="font-body text-sm text-stone-600 italic leading-snug">“{n}”</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoubleDownMealMap;
